/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from '../../components/layouts/SearchBox';
import { MultiComboBox } from '../../components/selects/ComboBox';
import { useTranslation } from 'react-i18next';
import { Code } from '../../models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from '../../apis/admin/CommonCode';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from '../../components/layouts/ContentLayout';
import { Button } from '@mui/material';
import { IconButton } from '../../components/buttons/IconSVG';
import CustomGrid from '../../components/grids/CustomGrid';
import { CrudCode } from '../../models/common/Edit';
import { useMessageBar } from '../../hooks/useMessageBar';
import { CommonUtil } from '../../utils/CommonUtil';
import { useCommonModal } from '../../hooks/useCommonModal';
import { getMpMcrcList } from 'apis/mp/MpMcrc';
import useSessionStore from 'stores/useSessionStore';
import _ from 'lodash';
import { MpPlntList } from 'models/mp/MpPlnt';
import { getFactoryList } from 'apis/mp/MpFactory';

/**
 * Factory ID 조회 페이지
 */

interface SearchParam {
  plntTpCd: string;
  plntTpCds: any;
  copCd: string;
  copCds: any;
}

const MpFactoryPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const [copCdCode, setCopCdCode] = useState<Code[]>([]);
  const [plntTpCdCode, setPlntTpCdCode] = useState<Code[]>([]);

  const [errors, setErrors] = useState<any>({});

  const initSearchParam = {
    plntTpCd: '',
    plntTpCds: [],
    copCd: '',
    copCds: [],
  };

  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();

  const [rowData, setRowData] = useState<MpPlntList[]>([]);

  useEffect(() => {
    initCondition();
    btnSearch(searchParam);
  }, []);

  const initCondition = async () => {
    const copCd: Code[] = await getCommonCodeNames('ELM_COP_CD');
    const plntTpCd: Code[] = await getCommonCodeNames('PLNT_TP_CD');

    setCopCdCode(copCd);
    setPlntTpCdCode(plntTpCd);
  };

  const btnSearch = (searchParam) => {
    getFactoryList(searchParam).then((result: MpPlntList[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const handleOnChange = (name: string, value: string) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
    setErrors({ ...errors, [name]: false });
  };

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'copCd',
        header: String(t('mp.label.법인', '법인')),
        width: 220,
        align: 'center',
      },
      {
        binding: 'prdnPlntCd',
        header: String(t('mp.label.Factory ID', 'Factory ID')),
        width: 220,
        align: 'center',
      },
      {
        binding: 'prdnPlntNm',
        header: String(t('mp.label.Factory 명', 'Factory 명')),
        width: '*',
        align: 'left',
      },
      {
        binding: 'plntTpCdNm',
        header: String(t('mp.label.Factory 유형', 'Factory 유형')),
        width: 220,
        align: 'center',
      },
      {
        binding: 'useYn',
        header: String(t('mp.label.사용여부', '사용여부')),
        width: 150,
        align: 'center',
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 150,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 150,
        align: 'center',
        isReadOnly: true,
      },
    ];
  }, [rowData]);

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: initSearchParam[cur] || '' }),
          {}
        ) as SearchParam
    );
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('mp.label.법인', '법인'))}</label>
                <MultiComboBox
                  id="elmCopCd"
                  options={copCdCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.copCds || []}
                  onChange={(value) => {
                    handleOnChange('copCd', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.Factory 유형', 'Factory 유형'))}</label>
                <MultiComboBox
                  id="plntTpCd"
                  options={plntTpCdCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.plntTpCds || []}
                  onChange={(value) => {
                    handleOnChange('plntTpCd', value.join());
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button
              css={IconButton.button}
              className="find"
              onClick={() => btnSearch(searchParam)}
              disableRipple
            >
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.label.Factory ID 목록', 'Factory ID 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={() => {
              CommonUtil.exportWijmoExcelGrid(
                flexRef,
                t('mp.label.Factory ID 목록', 'Factory ID 목록'),
                t('mp.label.Factory ID 목록', 'Factory ID 목록')
              );
            }}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        isSelector={false}
        isReadOnly={true}
        height={620}
        isFilter={true}
        allowPinning={true}
      />
    </>
  );
};
export default MpFactoryPage;
